<template>
  <v-card outlined class="scoring-summary-card">
    <v-card-title class="scoring-summary-card__header white--text py-3 px-4">
      <span class="h6 font-weight-bold">{{
        $t('routines.scoring.summary')
      }}</span>
    </v-card-title>

    <v-card-text class="px-4 pt-4 pb-3">
      <p
        v-if="showMaxPointsNotice"
        data-testid="scoringSummaryCard-text-maxPointsUnavailable"
        class="body-2 blue-grey--text text--darken-1 text-center mb-0"
      >
        {{ $t('routines.scoring.max_points_unavailable') }}
      </p>

      <template v-else>
        <div data-testid="scoringSummaryCard-row-maxPoints" class="mb-4">
          <span
            class="d-block text-overline blue-grey--text font-weight-bold text-center"
          >
            {{ $t('routines.scoring.max_points') }}
          </span>
          <span
            class="d-block text-center scoring-summary-card__score-value font-weight-bold blue--text text--darken-1 line-height-1 mb-4"
          >
            {{ localizedMaxPoints }}
          </span>
        </div>

        <div v-if="hasSections" class="d-flex justify-end mb-2">
          <span
            class="caption blue-grey--text text--darken-2 font-weight-medium"
          >
            {{ $t('routines.scoring.weight_label') }}
          </span>
        </div>

        <section
          v-for="(section, index) in sectionRows"
          class="mb-4"
          :key="section.id || index"
        >
          <div class="d-flex justify-space-between align-center">
            <span
              cols=""
              class="body-3 font-weight-500 blue-grey--text text--darken-2"
            >
              {{ section.label }}
            </span>

            <span
              cols="4"
              class="body-3 blue-grey--text text--darken-1 text-end"
            >
              {{ section.percent }}%
            </span>
          </div>

          <v-progress-linear
            background-color="blue-grey lighten-2"
            rounded
            :color="section.color"
            :height="6"
            :value="section.percent"
          />
        </section>

        <v-divider v-if="hasSections" class="my-4" />

        <div
          v-if="hasSections"
          data-testid="scoringSummaryCard-row-totalWeight"
          class="d-flex justify-space-between align-center body-3 blue-grey--text text--darken-2 font-weight-bold"
        >
          <span data-testid="scoringSummaryCard-text-totalWeightLabel">
            {{ $t('common.total') }}
          </span>

          <span data-testid="scoringSummaryCard-text-totalWeightValue">
            {{ totalSectionWeight }}%
          </span>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { formatLocalizedPoints } from '@/lib/formatLocalizedPoints'
import { getChecklistSectionColor } from '@/lib/getChecklistSectionColor'

export default {
  name: 'ScoringSummaryCard',

  props: {
    summary: {
      type: Object,
      default: () => ({
        scorePercentage: null,
        totalPoints: null,
      }),
    },
    sections: {
      type: Array,
      default: () => [],
    },
    hasItemsWithVisibilityRules: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    showMaxPointsNotice() {
      return this.hasItemsWithVisibilityRules
    },

    hasSections() {
      return this.sections.length > 0
    },

    maxPoints() {
      return this.summary?.totalPoints ?? 0
    },

    localizedMaxPoints() {
      return formatLocalizedPoints(this.maxPoints)
    },

    sectionRows() {
      return this.sections.map((section, index) => {
        const hasWeight = ![null, undefined, ''].includes(
          section?.weightPercentage
        )
        const normalizedWeight = Number(section?.weightPercentage)
        const weight =
          hasWeight && !Number.isNaN(normalizedWeight) ? normalizedWeight : 0
        const points = this.pointsForWeight(weight)

        return {
          id: section?.id || index,
          label: section?.title,
          pointsLabel: `${formatLocalizedPoints(points)} ${this.$t(
            'routines.scoring.points_abbreviation'
          )}`,
          percent: weight,
          color: getChecklistSectionColor(index),
        }
      })
    },

    totalSectionWeight() {
      return this.sectionRows.reduce((totalWeight, section) => {
        return totalWeight + section.percent
      }, 0)
    },
  },

  methods: {
    pointsForWeight(weight) {
      if (!this.maxPoints) {
        return 0
      }

      return Math.round((this.maxPoints * weight) / 100)
    },
  },
}
</script>

<style scoped lang="scss">
.scoring-summary-card {
  border-color: $color-blue-grey-lighten2 !important;
  border-radius: 4px;
}

.scoring-summary-card__header {
  background-color: $color-blue;
}

.line-height-1 {
  line-height: 1;
}

.scoring-summary-card__score-value {
  font-size: 44px;
}
</style>
